import React from 'react'
import './skeleton.scss'

interface SkeletonProps {
  width?: string
  height?: string
  borderRadius?: string
  className?: string
}

const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  borderRadius,
  className = ''
}) => {
  const style: React.CSSProperties = {}
  if (width) style.width = width
  if (height) style.height = height
  if (borderRadius) style.borderRadius = borderRadius
  return <div className={`skeleton ${className}`} style={style} />
}

interface CardSkeletonProps {
  lines?: number
  showFooter?: boolean
  className?: string
}

const CardSkeleton: React.FC<CardSkeletonProps> = ({
  lines = 2,
  showFooter = true,
  className = ''
}) => {
  return (
    <div className={`skeleton-card ${className}`}>
      <div className="skeleton-card__header">
        <Skeleton width="70px" height="12px" borderRadius="3px" />
        <Skeleton width="90px" height="12px" />
      </div>

      <Skeleton width="55%" height="16px" className="skeleton-card__title" />

      <div className="skeleton-card__info-row">
        <Skeleton width="100px" height="12px" />
        <Skeleton width="60px" height="14px" borderRadius="3px" />
      </div>

      <div className="skeleton-card__amount-box">
        <Skeleton width="90px" height="10px" />
        <Skeleton width="120px" height="18px" />
      </div>

      <div className="skeleton-card__body">
        {Array.from({ length: lines }, (_, i) => (
          <div key={i} className="skeleton-card__row">
            <Skeleton width="35px" height="11px" />
            <Skeleton width="90px" height="11px" />
          </div>
        ))}
      </div>

      {showFooter && (
        <div className="skeleton-card__footer">
          <Skeleton width="100px" height="14px" borderRadius="3px" />
          <div className="skeleton-card__footer-actions">
            <Skeleton width="80px" height="28px" borderRadius="6px" />
            <Skeleton width="50px" height="28px" borderRadius="6px" />
          </div>
        </div>
      )}
    </div>
  )
}

const CELL_WIDTHS = ['60%', '75%', '50%', '85%', '65%', '45%', '70%', '55%']

interface TableSkeletonProps {
  rows?: number
  columns?: number
  showHeader?: boolean
  className?: string
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  className = ''
}) => {
  return (
    <div className={`skeleton-table ${className}`}>
      {showHeader && (
        <div className="skeleton-table__header">
          {Array.from({ length: columns }, (_, i) => (
            <div key={i} className="skeleton-table__header-cell">
              <Skeleton width="70%" height="10px" />
            </div>
          ))}
        </div>
      )}

      <div className="skeleton-table__body">
        {Array.from({ length: rows }, (_r, rowIdx) => (
          <div key={rowIdx} className="skeleton-table__row">
            {Array.from({ length: columns }, (_c, colIdx) => (
              <div key={colIdx} className="skeleton-table__cell">
                <Skeleton
                  width={CELL_WIDTHS[(rowIdx + colIdx) % CELL_WIDTHS.length]}
                  height="14px"
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

interface StatSkeletonProps {
  count?: number
  className?: string
}

const StatsSkeleton: React.FC<StatSkeletonProps> = ({
  count = 3,
  className = ''
}) => {
  return (
    <div className={`skeleton-stats ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton-stats__card">
          <Skeleton width="24px" height="24px" borderRadius="6px" />
          <Skeleton width="50px" height="28px" />
          <Skeleton width="90px" height="10px" />
        </div>
      ))}
    </div>
  )
}

export { Skeleton, CardSkeleton, TableSkeleton, StatsSkeleton }
export default Skeleton
